import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A value that holds for `durationMs` after it is shown, then clears itself:
 * a "copied" check, a jumped-to row's flash, a focused field's highlight.
 * Showing again restarts the clock. Cleared on unmount.
 */
export function useTransientValue<T>(
  durationMs: number,
): [value: T | null, show: (value: T) => void, clear: () => void] {
  const [value, setValue] = useState<T | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    setValue(null);
  }, []);

  const show = useCallback(
    (next: T) => {
      if (timer.current !== null) clearTimeout(timer.current);
      setValue(next);
      timer.current = setTimeout(() => {
        timer.current = null;
        setValue(null);
      }, durationMs);
    },
    [durationMs],
  );

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return [value, show, clear];
}

/** `useTransientValue` for a yes/no: on for `durationMs` after `trigger`. */
export function useTransientFlag(
  durationMs: number,
): [on: boolean, trigger: () => void, clear: () => void] {
  const [value, show, clear] = useTransientValue<true>(durationMs);
  const trigger = useCallback(() => show(true), [show]);
  return [value === true, trigger, clear];
}
